'use client';

import { useState } from 'react';
import styled from 'styled-components';
import { IconTrainline } from '@lmn/lastminute-react-icons/lastminute-icons-solid.js';
import { IconBusline, IconPlaneNe } from '@/app/common/icons/libraryIcons';
import { getAirlineLogoUrl } from '@/app/common/contentHubAssets.ts';

const FALLBACK_ICONS = {
  FLIGHT: IconPlaneNe,
  TRAIN: IconTrainline,
  BUS: IconBusline,
};

export type AirlineLogoProps = {
  /** IATA code of the carrier, e.g. `FR`. Its logo is looked up in the DAM by code. */
  code?: string;
  /** Carrier name, used as the image's alternative text. */
  name?: string;
  /** Rendered width and height in px. */
  size?: number;
  /** Width and height of the fallback icon in px. Defaults to two thirds of `size`. */
  iconSize?: number;
  /** Segment type, as it comes from the transport data. Matched case-insensitively
   *  because the search payload spells it in lower case where the transport ones do
   *  not; anything other than a train or a coach falls back to the plane icon. */
  transportType?: string;
  className?: string;
};

// The container carries no colours of its own so that the icon takes the
// surrounding text colour and the call site's own badge or circle keeps showing
// through.
const FallbackContainer = styled.span<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
`;

/**
 * A carrier's logo, falling back to the transport-type icon when there is none to
 * show. The DAM does not hold an icon for every carrier — small and charter
 * airlines are regularly missing, and the placeholder codes we blacklist never had
 * one — so without a fallback the request 404s and the browser paints its own
 * broken-image glyph in the middle of the itinerary (MPH-941).
 *
 * `@lmn/lmn-components` ships a `TransportLogo` with the same three icons and the
 * same error flow, and the results-page popover still uses it through
 * `FullTransportLogos`. This one exists rather than wrapping it because it skips
 * the request altogether for the codes we blacklist instead of waiting on a 404,
 * names the carrier for screen readers instead of labelling the image with its
 * IATA code, takes a `className` so call sites can round the corners or nudge the
 * baseline, leaves the box transparent so the badge or circle behind it shows
 * through, and picks the plane for an unrecognised transport type where the
 * library indexes its icon map unguarded and renders `undefined`. Worth folding
 * back into the library if someone gets the chance.
 */
export function AirlineLogo({ code, name = '', size = 24, iconSize, transportType, className }: AirlineLogoProps) {
  const logoUrl = getAirlineLogoUrl(code);
  // Keyed on the url rather than a plain boolean so that a card reused for
  // another carrier (the change-flight list pages in place) starts over instead
  // of inheriting the previous carrier's failure.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (!logoUrl || failedUrl === logoUrl) {
    const FallbackIcon = FALLBACK_ICONS[transportType?.toUpperCase() as keyof typeof FALLBACK_ICONS] ?? IconPlaneNe;
    // Without a name the icon carries nothing a screen reader could not read from
    // the row itself, so it is announced only when there is a carrier to name.
    const labelling = name ? { role: 'img', 'aria-label': name } : { 'aria-hidden': true };
    return (
      <FallbackContainer $size={size} className={className} {...labelling}>
        <FallbackIcon size={iconSize ?? Math.round((size * 2) / 3)} />
      </FallbackContainer>
    );
  }

  return (
    <img
      className={className}
      src={logoUrl}
      width={size}
      height={size}
      alt={name}
      onError={() => setFailedUrl(logoUrl)}
    />
  );
}
